import { useState, useEffect } from 'react'

/**
 * Hook to detect if screen matches a media query
 * @param {string} query - Media query string (e.g., "(max-width: 768px)")
 * @returns {boolean} - True if media query matches
 */
export function useMediaQuery(query) {
  const [matches, setMatches] = useState(false)

  useEffect(() => {
    const media = window.matchMedia(query)
    
    // Set initial value
    if (media.matches !== matches) {
      setMatches(media.matches)
    }
    
    // Listen for changes
    const listener = () => setMatches(media.matches)
    
    // Modern browsers
    if (media.addEventListener) {
      media.addEventListener('change', listener)
      return () => media.removeEventListener('change', listener)
    } else {
      // Fallback for older browsers
      media.addListener(listener)
      return () => media.removeListener(listener)
    }
  }, [query])

  return matches
}

/**
 * Hook to detect mobile devices (screen width < 768px)
 * @returns {boolean} - True if mobile
 */
export function useIsMobile() {
  return useMediaQuery('(max-width: 768px)')
}

/**
 * Hook to detect tablet devices (768px <= screen width < 1024px)
 * @returns {boolean} - True if tablet
 */
export function useIsTablet() {
  return useMediaQuery('(min-width: 768px) and (max-width: 1023px)')
}

/**
 * Hook to detect mobile OR tablet devices (screen width < 1024px)
 * Use this for iPads and other tablets to get touch-friendly layout
 * @returns {boolean} - True if mobile or tablet
 */
export function useIsMobileOrTablet() {
  return useMediaQuery('(max-width: 1023px)')
}

/**
 * Hook to detect touch-capable devices
 * @returns {boolean} - True if touch device
 */
export function useIsTouchDevice() {
  return useMediaQuery('(pointer: coarse)')
}
